import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { FaQuestionCircle } from "react-icons/fa";
import "./TopicsTrendsPage.scss";
import DataWindowSlider from "../components/DataWindowSlider";
import { useDataWindow } from '../hooks/useDataWindow';
import debounce from 'lodash/debounce';
import { getMarkerLabel } from "./markersDictionary";
import ChartsGroup from "../components/ChartsGroup";
import { toJS } from "mobx";
import { fillDataGaps, getActualDataLength } from '../utils/dataUtils';
import { useGlobalFilters } from '../hooks/useGlobalFilters';

const AGGREGATION_OPTIONS = [
  { value: 1, label: "По дням" },
  { value: 7, label: "Суммировать по неделям" },
  { value: 30, label: "Суммировать по месяцам" },
];

const MARKERS_KEY = 'selectedMarkers_topics';

const TopicsTrendsPage = observer(({store, themes}) => {
  const [selectedMarkers, setSelectedMarkers] = useState(() => {
    try {
      const saved = localStorage.getItem(MARKERS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const globalFilters = useGlobalFilters();

  // Обработка данных с заполнением пропусков
  const processedChartData = useMemo(() => {
    if (!store.chartsData || store.chartsData.length === 0) {
      return [];
    }

    // Получаем фильтры из глобальных фильтров или store
    const globalStartDate = globalFilters.startDate;
    const globalEndDate = globalFilters.endDate;
    const storeStartDate = store.globalFilters?.startDate;
    const storeEndDate = store.globalFilters?.endDate;
    
    const startDate = globalStartDate || storeStartDate || store.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const endDate = globalEndDate || storeEndDate || store.endDate || new Date().toISOString().split('T')[0];

    return fillDataGaps(
      toJS(store.chartsData), 
      startDate, 
      endDate, 
      store.aggregation.toString()
    );
  }, [store.chartsData, store.startDate, store.endDate, store.aggregation, globalFilters, store.globalFilters]);

  // Используем отдельный ключ хранения, чтобы окно не пересекалось с другими страницами
  const [dataWindow, updateDataWindow] = useDataWindow(100, getActualDataLength(processedChartData), 'topicsDataWindow');

  const markerColors = useMemo(() => {
    const colors = [
      '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
      '#0288d1', '#c2185b', '#fbc02d', '#455a64', '#0097a7',
    ];
    const colorMap = {};
    selectedMarkers.forEach((marker, index) => {
      colorMap[marker] = colors[index % colors.length];
    });
    return colorMap;
  }, [selectedMarkers]);

  

  // Дебансированная версия fetchTopicsTrendsData
  const debouncedFetchTopicsTrendsData = useMemo(
    () => debounce((start, end) => store.fetchTopicsTrendsData(start, end), 300),
    [store]
  );

  // Очистка дебансированной функции при размонтировании
  useEffect(() => {
    return () => {
      debouncedFetchTopicsTrendsData.cancel();
    };
  }, [debouncedFetchTopicsTrendsData]);

  // Загрузка групп при монтировании
  useEffect(() => {
    store.fetchGroups();
  }, []);

  // Автоматическая загрузка данных при изменении фильтров
  useEffect(() => {
    debouncedFetchTopicsTrendsData(dataWindow.start, dataWindow.end);
  }, [
    store.aggregation,
    store.selectedThemes,
    store.groupMode,
    store.selectedTheme,
    store.globalFilters,
    dataWindow,
  ]);

  // Синхронизация глобальных фильтров из localStorage
  useEffect(() => {
    let last = JSON.stringify(store.globalFilters);
    const interval = setInterval(() => {
      const saved = localStorage.getItem("globalFilters");
      if (saved && saved !== last) {
        last = saved;
        try {
          const parsed = JSON.parse(saved);
          store.setGlobalFilters({
            selectedGroups: Array.isArray(parsed.selectedGroups) ? parsed.selectedGroups : [],
            startDate: parsed.startDate,
            endDate: parsed.endDate,
          });
        } catch {}
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Обработчики
  const handleThemeToggle = (key) => {
    if (store.groupMode === "sum") {
      store.setThemes(
        store.selectedThemes.includes(key)
          ? store.selectedThemes.filter((k) => k !== key)
          : [...store.selectedThemes, key]
      );
    }
  };

  const handleThemeSelect = (key) => {
    if (store.groupMode === "separate") {
      store.setSelectedTheme(key);
      store.setThemes([key]);
    }
  };

  // Проверка на пустые темы
  useEffect(() => {
    if (store.selectedThemes.length === 0) {
      const available = themes.map(t => t.key);
      const filtered = ["trager_coef", "tgw_positive_emotions"].filter((key) => available.includes(key));
      store.setThemes(filtered.length === 2 ? filtered : ["trager_coef", "tgw_positive_emotions"]);
    }
  }, [store.selectedThemes]);

  return (
    <div className="topics-trends-container">
      <div
        className="filters-panel"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 32,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            gap: 12,
            minWidth: 220,
            width: 340,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
            <div className="filter-group">
              <label className="filter-label">Агрегация:</label>
              <select
                value={store.aggregation}
                onChange={(e) => store.setAggregation(Number(e.target.value))}
                className="select select--wide"
                style={{ height: 36, minWidth: 180 }}
              >
                {AGGREGATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Режим:</label>
              <select
                value={store.groupMode}
                onChange={(e) => store.setGroupMode(e.target.value)}
                className="select select--wide"
                style={{ height: 36, minWidth: 180 }}
              >
                <option value="sum">Среднее по сообществам</option>
                <option value="separate">Отдельно по сообществам</option>
              </select>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div className="themes-container" style={{ flex: 1, minWidth: 0 }}>
            {store.groupMode === "sum" ? (
              themes.map((theme) => (
                <div key={theme.key} className="theme-item">
                  <label className="theme-checkbox">
                    <input
                      type="checkbox"
                      checked={store.selectedThemes.includes(theme.key)}
                      onChange={() => handleThemeToggle(theme.key)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-label">{theme.label}</span>
                  </label>
                  <div className="help-icon" title={theme.description}>
                    <FaQuestionCircle />
                  </div>
                </div>
              ))
            ) : (
              <div className="filter-group">
                <label className="filter-label">Тема:</label>
                <select
                  value={store.selectedTheme}
                  onChange={(e) => handleThemeSelect(e.target.value)}
                  className="select"
                >
                  {themes.map((theme) => (
                    <option key={theme.key} value={theme.key}>
                      {theme.label}
                    </option>
                  ))}
                </select>
                <div className="help-icon" title={themes.find(t => t.key === store.selectedTheme)?.description || ""}>
                  <FaQuestionCircle />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="chart-container">
        { store.globalFilters.selectedGroups.length === 0? (
          <p className="no-data">Выберите сообщества в верхней панели для отображения графика</p>
        ) : store.error ? (
          <div className="error-message">{store.error}</div>
        ) : processedChartData.length === 0 ? (
          store.loading ? (
              <div className="no-data-message">Загрузка</div>
            ) : (
            <div className="no-data-message">Нет данных для отображения</div>
          )          
        ) : (
          <>             
            <DataWindowSlider
              aggregation={store.aggregation}
              actualDataLength={getActualDataLength(processedChartData)}
              storageKey="topicsDataWindow"
            />            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <ChartsGroup
                loading = {store.loading}
                chartData={processedChartData}
                dataWindow={dataWindow}
                markerColors={markerColors}
                getMarkerLabelFunction={getMarkerLabel}
                getTableDataByDate={(date) => store.getTableDataByDate(date)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default TopicsTrendsPage;